// Consent manager — first-party, versioned, no identifiers stored.
//
// Storage: localStorage 'tfg_consent' (essential — remembers the choice itself).
// A record is valid for CONSENT_MAX_AGE_DAYS and only for the current CONSENT_VERSION;
// otherwise the banner is shown again.
//
// Vendors run only when BOTH the production-host gate (src/lib/env.ts) and the
// relevant consent category allow it. See src/lib/consentRuntime.ts.

export const CONSENT_VERSION = 1;
export const CONSENT_MAX_AGE_DAYS = 365;
const STORAGE_KEY = 'tfg_consent';

export type ConsentCategory =
  | 'analytics'
  | 'experience_analytics'
  | 'business_identification'
  | 'marketing';

export interface ConsentChoices {
  analytics: boolean;
  experience_analytics: boolean;
  business_identification: boolean;
  marketing: boolean;
}

export interface ConsentRecord extends ConsentChoices {
  consent_version: number;
  timestamp: string;
  essential: true;
  /** True if a Global Privacy Control signal was present when the choice was saved. */
  gpc: boolean;
}

/** Categories treated as "sale/sharing / targeted" opt-outs when GPC is on. */
export const GPC_CATEGORIES: readonly ConsentCategory[] = ['business_identification', 'marketing'];

export const DENIED: ConsentChoices = {
  analytics: false,
  experience_analytics: false,
  business_identification: false,
  marketing: false,
};

export function hasGPC(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
}

function isValid(r: unknown): r is ConsentRecord {
  if (!r || typeof r !== 'object') return false;
  const rec = r as ConsentRecord;
  if (rec.consent_version !== CONSENT_VERSION) return false;
  const ts = Date.parse(rec.timestamp);
  if (Number.isNaN(ts)) return false;
  if (Date.now() - ts > CONSENT_MAX_AGE_DAYS * 86_400_000) return false;
  return true;
}

let current: ConsentRecord | null = null;
let loaded = false;

function load(): ConsentRecord | null {
  if (loaded) return current;
  loaded = true;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    current = isValid(parsed) ? parsed : null;
  } catch {
    current = null;
  }
  // A GPC signal that appears after a stored opt-in still wins for GPC categories.
  if (current && hasGPC()) {
    for (const c of GPC_CATEGORIES) current[c] = false;
  }
  return current;
}

/** Stored record, or null when the visitor has not made a (still valid) choice. */
export function getConsentRecord(): ConsentRecord | null {
  return load();
}

/** Effective choices — everything non-essential is denied until the visitor decides. */
export function getConsent(): ConsentChoices {
  const r = load();
  if (!r) return { ...DENIED };
  return {
    analytics: r.analytics,
    experience_analytics: r.experience_analytics,
    business_identification: r.business_identification,
    marketing: r.marketing,
  };
}

export function hasDecided(): boolean {
  return load() !== null;
}

type Listener = (choices: ConsentChoices, previous: ConsentChoices) => void;
const listeners = new Set<Listener>();

export function onConsentChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function saveConsent(choices: ConsentChoices) {
  const previous = getConsent();
  const record: ConsentRecord = {
    consent_version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    essential: true,
    analytics: !!choices.analytics,
    experience_analytics: !!choices.experience_analytics,
    business_identification: !!choices.business_identification,
    marketing: !!choices.marketing,
    gpc: hasGPC(),
  };
  current = record;
  loaded = true;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    /* storage blocked — choice applies for this page only */
  }
  const next = getConsent();
  listeners.forEach((fn) => {
    try {
      fn(next, previous);
    } catch {
      /* ignore */
    }
  });
}

/** "Accept All" — GPC categories stay off while a GPC signal is present. */
export function acceptAllChoices(): ConsentChoices {
  const gpc = hasGPC();
  return {
    analytics: true,
    experience_analytics: true,
    business_identification: !gpc,
    marketing: !gpc,
  };
}

// ---- Preference-center open requests (footer "Privacy Choices") ----
const OPEN_EVENT = 'tfg:open-privacy-preferences';
export function openPrivacyPreferences() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}
export function onOpenPrivacyPreferences(fn: () => void): () => void {
  window.addEventListener(OPEN_EVENT, fn);
  return () => window.removeEventListener(OPEN_EVENT, fn);
}
